import JsBarcode from "jsbarcode";
import moment from "moment";

import { getDataURLFromURL } from "react-lib/apps/HISV3/common/CommonInterface";

import {
  HeaderLogoWithAddress,
  HeaderLogoWithTitleContent,
} from "../HISV3/common/HeaderPdfFormTemplate";

import { beToAd, formatDate, formatTime } from "react-lib/utils/dateUtils";

import CONFIG from "config/config";

const FORM_NAME = "FormPhysicalTherapyAppointment";

type FormPhysicalTherapyAppointmentProps = {
  data?: {
    appointment_time?: string;
    attending_therapist?: string;
    date?: string;
    division_name?: string;
    divisionEN?: string;
    divisionQR?: string;
    doctor_note?: string[];
    full_age?: string;
    full_name?: string;
    fullAgeEN?: string;
    fullNameEN?: string;
    items?: { name: string; number: string; suffix: string }[];
    language?: "EN" | "TH";
    patient_hn?: string;
    referring_physician?: string;
    referringPhysicianEN?: string;
    staff?: string;
    staffEN?: string;
  };
};

// Language translations
const translations = {
  EN: {
    additionalAdvice: "Additional advice :",
    age: "Age :",
    appointmentDate: "Appointment date :",
    appointmentTime: "Appointment time :",
    attendingTherapist: "Attending Physical Therapist :",
    department: "Department :",
    facultyName: "Faculty of Dentistry Chulalongkorn University",
    hn: "HN :",
    patientName: "Patient Name :",
    physicalTherapyProgram: "Physical Therapy Program :",
    printDateTime: "Print Date-time",
    printedBy: "Printed by -",
    qrCodeContact: "QR code \nfor contact",
    referringPhysician: "Referring Physician :",
    title: "Physical Therapy Appointment",
  },
  TH: {
    additionalAdvice: "คำแนะนำสำหรับผู้ป่วย :",
    age: "อายุ :",
    appointmentDate: "วันที่นัด :",
    appointmentTime: "เวลานัด :",
    attendingTherapist: "นักกายภาพบำบัดที่ดูแล :",
    department: "แผนก :",
    facultyName: "โรงพยาบาลคณะทันตแพทยศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย",
    hn: "HN :",
    patientName: "ชื่อ - นามสกุล :",
    physicalTherapyProgram: "โปรแกรมการรักษา :",
    printDateTime: "วัน-เวลาที่พิมพ์",
    printedBy: "ผู้พิมพ์ -",
    qrCodeContact: "QR code \nติดต่อ",
    referringPhysician: "แพทย์ผู้ส่งต่อ :",
    title: "ใบนัดหมายกายภาพบำบัด",
  },
};

const convertTextToBase64BarcodeBlob = (text: string) => {
  const canvas = document.createElement("canvas");

  JsBarcode(canvas, text, { displayValue: false });

  return canvas.toDataURL("image/png");
};

const FormPhysicalTherapyAppointment = async (props: FormPhysicalTherapyAppointmentProps) => {
  console.log("FormPhysicalTherapyAppointment props.data:", props.data);

  const language = props.data?.language || "TH";
  const t = translations[language];

  const [divisionQR] = await getDataURLFromURL([{ image: props.data?.divisionQR || "" }]);

  const getPatientFullName = (): string => {
    if (language === "EN") {
      return props.data?.fullNameEN || props.data?.full_name || "";
    }

    return props.data?.full_name || "";
  };

  const getPatientAge = (): string => {
    if (language === "EN") {
      return props.data?.fullAgeEN || "";
    }

    return props.data?.full_age || "";
  };

  const getReferringPhysician = (): string => {
    const isEnglish = language === "EN";
    const hasEnglishPhysician = props.data?.referringPhysicianEN?.trim() !== "";

    if (isEnglish && hasEnglishPhysician) {
      return props.data?.referringPhysicianEN || "";
    }

    return props.data?.referring_physician || "";
  };

  const getFormattedAppointmentDate = (): string => {
    if (!props.data?.date) {
      return "";
    }

    if (language === "EN") {
      const year = Number(moment(props.data.date, "DD/MM/YYYY").format("YYYY")) - 543;

      return `${moment(props.data.date, "DD/MM/YYYY").format("MMMM D")}, ${year}`;
    }

    const formattedDate = beToAd(props.data.date)?.clone().locale("th").format("DD MMMM");
    const year = moment(props.data.date, "DD/MM/YYYY").format("YYYY");

    return `${formattedDate} ${year}`;
  };

  const getFormattedAppointmentTime = (): string => {
    if (props.data?.appointment_time) {
      const formattedTime = formatTime(props.data.appointment_time);

      return language === "EN" ? formattedTime.time_en : formattedTime.time_th;
    }

    return "";
  };

  const getDepartmentName = (): string => {
    if (language === "EN") {
      return props.data?.divisionEN || props.data?.division_name || "";
    }

    return props.data?.division_name || "";
  };

  const getPrintDateTime = (): string =>
    language === "EN" ? moment().format("DD/MM/YYYY") : formatDate(moment());

  const getPrintedByText = (): string => {
    const staff = language === "EN" ? props.data?.staffEN || "" : props.data?.staff || "";

    return `${t.printedBy} ${staff}`;
  };

  const getAppointmentTimeWidth = () => (language === "EN" ? "18.5%" : "8%");

  const getAppointmentTimeValueWidth = () => (language === "EN" ? "15%" : "21%");

  const headerForm =
    CONFIG.COMPANY === "SAPIENS"
      ? await HeaderLogoWithAddress({
          font: "THSarabunNew",
          form: FORM_NAME,
          headerMargins: [10, 5, 10, 5],
          logoHeight: 30,
          pageMargins: [10, 110, 10, CONFIG.HIDE_APPOINTMENT_BARCODE ? 25 : 55],
          styles: {
            facultyHeader: {
              bold: true,
              fontSize: 20,
            },
            fieldKey: {
              bold: true,
              fontSize: 15,
            },
            fieldValue: {
              fontSize: 15,
            },
            miniFieldKey: {
              bold: true,
              fontSize: 12.5,
            },
            miniFieldValue: {
              fontSize: 13,
            },
          },
          titleContent: [
            {
              alignment: "center",
              style: "facultyHeader",
              text: t.title,
            },
          ],
        })
      : await HeaderLogoWithTitleContent({
          font: "THSarabunNew",
          form: FORM_NAME,
          headerMargins: [10, 5, 10, 5],
          logoHeight: 40,
          logoPosition: { x: 10, y: 8 },
          pageMargins: [10, 60, 10, 75],
          styles: {
            facultyHeader: {
              bold: true,
              fontSize: 20,
            },
            fieldKey: {
              bold: true,
              fontSize: 15,
            },
            fieldValue: {
              fontSize: 15,
            },
            miniFieldKey: {
              bold: true,
              fontSize: 12.5,
            },
            miniFieldValue: {
              fontSize: 13,
            },
          },
          titleContent: [
            {
              alignment: "center",
              style: "facultyHeader",
              text: t.title,
            },
          ],
          titleMargins: [80, 3, 0, 0],
          titleName: t.facultyName,
        });

  const { font, images } = headerForm;

  const getContentHeader = () => [
    {
      columns: [
        {
          style: "fieldKey",
          text: t.patientName,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getPatientFullName(),
          width: "40%",
        },
      ],
    },
    {
      columns: [
        { style: "fieldKey", text: t.hn, width: "27.5%" },
        { style: "fieldValue", text: "", width: "1%" },
        { style: "fieldValue", text: props.data?.patient_hn || "", width: "40%" },
        {
          style: "fieldKey",
          text: t.age,
          width: "4.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getPatientAge(),
          width: "*",
        },
      ],
    },
    {
      columns: [
        {
          style: "fieldKey",
          text: t.referringPhysician,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getReferringPhysician(),
          width: "*",
        },
      ],
    },
    {
      columns: [
        {
          style: "fieldKey",
          text: t.appointmentDate,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getFormattedAppointmentDate(),
          width: "40%",
        },
        {
          style: "fieldKey",
          text: t.appointmentTime,
          width: getAppointmentTimeWidth(),
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getFormattedAppointmentTime(),
          width: getAppointmentTimeValueWidth(),
        },
      ],
      margin: [0, 5, 0, 0],
    },
    {
      columns: [
        {
          style: "fieldKey",
          text: t.department,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: getDepartmentName(),
          width: "*",
        },
      ],
      margin: [0, 5, 0, 0],
    },
    // Physical Therapy specific fields
    {
      columns: [
        {
          style: "fieldKey",
          text: t.physicalTherapyProgram,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
      ],
      margin: [0, 5, 0, 0],
    },
    // Program list as bullet points
    {
      margin: [20, 0, 0, 0],
      ul: (props.data?.items || []).map((program) => ({
        style: "fieldValue",
        text: [
          { text: program.number },
          {
            sup: true,
            text: program.suffix,
          },
          { text: ` ${program.name}` },
        ],
      })),
    },
    {
      columns: [
        {
          style: "fieldKey",
          text: t.attendingTherapist,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "1%" },
        {
          style: "fieldValue",
          text: props.data?.attending_therapist || "",
          width: "*",
        },
      ],
      margin: [0, 5, 0, 0],
    },
    {
      columns: [
        {
          style: "fieldKey",
          text: t.additionalAdvice,
          width: "27.5%",
        },
        { style: "fieldValue", text: "", width: "3%" },
      ],
      margin: [0, 5, 0, 0],
    },
    // Advice list as bullet points
    {
      margin: [20, 0, 0, 0],
      ul: (props.data?.doctor_note || []).map((advice: string) => ({
        style: "fieldValue",
        text: advice.replace(/^-/, ""),
      })),
    },
  ];

  const getFooterWithBarcode = () => [
    {
      margin: [20, 12.5, 0, 0],
      stack: [
        {
          height: 35,
          image: convertTextToBase64BarcodeBlob(props.data?.patient_hn || ""),
          width: 80,
        },
        {
          alignment: "center",
          bold: true,
          margin: [20, -2.5, 0, 0],
          style: "fieldValue",
          text: "HN",
        },
      ],
      width: 80,
    },
    {
      margin: [40, 12.5, 0, 0],
      stack: [
        {
          text: [
            {
              style: "miniFieldValue",
              text: `${t.printDateTime} ${getPrintDateTime()} `,
            },
            { style: "miniFieldValue", text: moment().format("HH:mm") },
          ],
        },
        {
          style: "miniFieldValue",
          text: getPrintedByText(),
        },
      ],
      width: "40%",
    },
    { text: "", width: "*" },
    {
      alignment: "center",
      columns: divisionQR.image
        ? [
            {
              margin: [0, 15, 0, 0],
              text: t.qrCodeContact,
            },
            {
              height: 60,
              image: divisionQR.image,
              width: 60,
            },
          ]
        : [],
      margin: [0, 2.5, 20, 20],
      width: "*",
    },
  ];

  const getFooterWithoutBarcode = () => [
    {
      margin: [20, 2.5, 0, 0],
      text: [
        {
          style: "miniFieldValue",
          text: `${t.printDateTime} ${getPrintDateTime()} `,
        },
        { style: "miniFieldValue", text: moment().format("HH:mm") },
        {
          style: "miniFieldValue",
          text: getPrintedByText(),
        },
      ],
    },
  ];

  return {
    defaultStyle: {
      font,
      fontSize: 13,
      lineHeight: 1,
    },
    pageOrientation: "landscape",
    pageSize: "A5",
    ...headerForm,
    images: {
      ...images,
      qrcode_line: `${origin}/static/images/qrcode_appointment_line.png`,
    },
    content: [
      {
        table: {
          widths: ["100%"],
          body: [
            [
              {
                margin: [20, 3, 10, 3],
                stack: getContentHeader(),
              },
            ],
          ],
        },
      },
    ],
    footer: {
      columns: CONFIG.HIDE_APPOINTMENT_BARCODE ? getFooterWithoutBarcode() : getFooterWithBarcode(),
    },
  };
};

export default FormPhysicalTherapyAppointment;
